import * as React from 'react';
import { SectionPanel } from 'ui/section-panel/section-panel';
import { MainGridContainer } from 'ui/common/main-grid-container/main-grid-container';
import { codetableOptionsIds } from 'app/area/codetable/item/screen-renderer/screen-pickers/constants';
import { ScreenPicker } from 'app/area/codetable/item/screen-renderer/types';
import { CodetableItemLoader } from 'app/area/shared/codetable/item/codetable-item-loader';
import { UomConversionProps } from 'app/area/codetable/item/uom-conversion/types';
import { UomConversionItems } from 'app/area/codetable/item/uom-conversion/uom-conversion-items';
import { CodetableItemRow } from 'app/area/codetable/list/list-items/common/codetable-item-row';
import { replaceTemplateString } from 'app/infrastructure/platform';
import { getFactorFieldIds, getInfoText } from 'app/area/codetable/item/uom-conversion/helpers';
import { CodeActionType } from 'app/area/codetable/item/types';
import styles from './uom-conversion.scss';
import { defaultUomFactorValue } from 'app/area/codetable/item/uom-conversion/constants';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';

export class UomConversion extends React.PureComponent<UomConversionProps> {

  public componentDidUpdate(prevProps: UomConversionProps) {
    const { codeItem: { category: { value: currCategory = undefined } = {} } = {},
      fetchingIsUomExcludedCheck: currFetchingIsUomExcludedCheck,
      codetableOptions: {
        unitOfMeasure: { codes: currUomCodes = undefined } = {}
      } = {}, isUomExcluded: currIsUomExcluded, loadCodetableOptions,
      updateUomConversion
    } = this.props;

    const { actionType, codeItem: { category: { value: prevCategory = undefined } = {} } = {},
      fetchingIsUomExcludedCheck: prevFetchingIsUomExcludedCheck,
      codetableOptions: {
        unitOfMeasure: { codes: prevUomCodes = undefined } = {}
      } = {}, isUomExcluded: prevIsUomExcluded
    } = prevProps;

    const categoryHasChanged = prevCategory !== currCategory;
    const uomCodesHasChanged = prevUomCodes !== currUomCodes;
    const shouldCreateUomConversion = prevFetchingIsUomExcludedCheck
      && !currFetchingIsUomExcludedCheck && prevIsUomExcluded !== currIsUomExcluded;
    const shouldClearUomConversion = actionType !== CodeActionType.Edit
      && prevIsUomExcluded !== currIsUomExcluded;

    if (categoryHasChanged) {
      this.clearFactorValidation();
      loadCodetableOptions(
        ScreenPicker.unitOfMeasure,
        codetableOptionsIds[ScreenPicker.unitOfMeasure],
        { categoryId: currCategory, includeInactiveCodes: { enabled: true } }
      );
    }

    uomCodesHasChanged && updateUomConversion(currUomCodes);
    shouldCreateUomConversion && this.createUomConversion();
    shouldClearUomConversion && this.clearUomConversion();
  }

  public componentWillUnmount() {
    this.props.clearUomConversionFactors();
    this.props.clearUomExcluded();
    this.clearFactorValidation();
  }

  private isReadonly = () => this.props.readOnlyConfig?.all;

  private clearFactorValidation = () => {
    const { clearFactorValidation, validation } = this.props;
    clearFactorValidation(getFactorFieldIds(validation));
  };

  private clearUomConversion = () => {
    const { clearByKey, clearOriginalCodeByKey } = this.props;

    clearByKey(ScreenPicker.uomConversion);
    clearOriginalCodeByKey(ScreenPicker.uomConversion);
  };

  private createUomConversion = () => {
    const { actionType, codetableId, codeItem: { id, category: { value: currCategory = undefined } = {},
      uomConversion } = {}, isUomExcluded: currIsUomExcluded,
      createUomConversionFactors, createInitialUomConversionFactors } = this.props;

    const emptyUomConversion = currIsUomExcluded && actionType !== CodeActionType.Edit;
    createInitialUomConversionFactors(uomConversion);
    createUomConversionFactors(id, codetableId, currCategory, actionType, emptyUomConversion ? [] : uomConversion);
  };

  private getInfoTextDescription = () => {
    const { codeItem: { category, description } = {},
      language: { value: languageValue } = {} } = this.props;

    return getInfoText({ category, description, languageValue });
  };

  private displayWarningMessage = () => {
    const { actionType, isUomExcluded, codeItem: { id } = {},
      initialUomConversionFactors, translation } = this.props;

    if (isUomExcluded || actionType !== CodeActionType.Edit || this.isReadonly()) {
      return null;
    }

    const hasConversionFactor = initialUomConversionFactors?.some(item => item.value === id);
    const hasConversionFactorWithDefaultValue = initialUomConversionFactors
      ?.some(item => item.value === id && item?.factor === defaultUomFactorValue);

    return !hasConversionFactor
      ? <div className="alert-warning mb-4">
        <p>{replaceTemplateString(
          translation.uom_conversion_factor_missing_conversion,
          this.getInfoTextDescription()
        )}</p>
      </div>
      : (!hasConversionFactorWithDefaultValue && <div className="alert-warning mb-4">
        <p>{replaceTemplateString(
          translation.uom_conversion_factor_wrong_factor,
          this.getInfoTextDescription()
        )}</p>
      </div>);
  };

  private getUomConversionFactors = () => {
    const { actionType, codeItem: { id } = {}, uomConversionFactors = [] } = this.props;

    if (actionType === CodeActionType.Duplicate) {
      return uomConversionFactors;
    }

    return uomConversionFactors?.filter(item => item?.value !== id) ?? [];
  };

  private displayUomConversion = () => {
    const { codeItem: { category } = {}, translation, isUomExcluded } = this.props;

    if (!!category && isUomExcluded) {
      return <p className="no-content">{
        replaceTemplateString(
          translation.uom_conversion_disclaimer_not_supperted_uom_excluded,
          category?.description
        )
      }</p>;
    }

    if (!category) {
      return <p className="no-content">{translation.uom_conversion_disclaimer_choose_category}</p>;
    }

    return this.getUomConversionFactors()?.length > 0
      ? <UomConversionItems {...this.props} />
      : <p className="no-content">{translation.uom_conversion_disclaimer_no_data}</p>;
  };

  private displayLoader = () => {
    return <div className={styles.loader}>
      <CodetableItemRow isLoader>
        <CodetableItemLoader>
          {this.props.translation.loading}
        </CodetableItemLoader>
      </CodetableItemRow>
    </div>;
  };

  public render() {
    const { sectionRef, isUomExcluded, translation, codeItem: { category } = {},
      fetchingIsUomExcludedCheck, fetchingUomConversionFactors } = this.props;

    const fetching = this.props?.codetableOptions?.unitOfMeasure?.fetching || fetchingIsUomExcludedCheck || fetchingUomConversionFactors;
    const infoTextDescription = this.getInfoTextDescription();

    return <div ref={sectionRef}>
      <SectionPanel
        className="admin-paper-top-panel"
        componentId="uom-conversion"
        validation={undefined}
        info={{
          visible: !fetching && !isUomExcluded && isDefinedIncludingZero(category?.value),
          text: infoTextDescription ? replaceTemplateString(
            translation.uom_conversion_factor_info,
            infoTextDescription
          ) : translation.uom_conversion_factor_info_default
        }}
        label={{ visible: true, text: translation.uom_conversion }} />
      <MainGridContainer>
        {!fetching && this.displayWarningMessage()}
        {!fetching ? this.displayUomConversion() : this.displayLoader()}
      </MainGridContainer>
    </div>;
  }
}
